import { createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { toast } from 'sonner'
import { Panel } from '@/components/panel'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { bareUrl, formatCount } from '@/lib/format'
import {
  type OutreachConfig,
  type OutreachConfigInput,
  useOutreachConfig,
  useOutreachTargets,
  usePinTarget,
  useSaveOutreachConfig,
  useSaveOutreachSecrets,
  useSetTargetEnabled,
} from '@/lib/outreach'
import type { Site } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { TableSkeleton, useSiteScope } from '../../-section'
import { FIELD, Field } from '../-parts'

export const Route = createFileRoute('/_app/$org/$host/outreach/_list/settings')({
  component: OutreachSettingsPage,
})

/**
 * What this site says in its own name, what it links to, and where the mail
 * would go out from. Sending is off until somebody says otherwise, and drafting
 * without sending is a state of its own: mail that has left cannot be unsent.
 */
function OutreachSettingsPage() {
  const { site } = useSiteScope()
  const { data, isPending } = useOutreachConfig(site)

  if (isPending || !data) {
    return (
      <>
        <h1 className="sr-only">Outreach settings</h1>
        <TableSkeleton rows={4} />
      </>
    )
  }

  const key = data.config?.updatedAt ?? 'new'

  return (
    <>
      <h1 className="sr-only">Outreach settings</h1>

      <div className="space-y-4">
        {/* Remounted on every save so the fields start from what was stored. */}
        <Sending siteId={site?.id} config={data.config} />
        <Identity
          key={`identity-${key}`}
          siteId={site?.id}
          config={data.config}
          maxDailyCap={data.limits.dailyCap}
        />
        <Targets key={`targets-${key}`} site={site} config={data.config} />
        <Finding key={`finding-${key}`} siteId={site?.id} config={data.config} />
        <Mailbox key={`mailbox-${key}`} siteId={site?.id} config={data.config} />
      </div>
    </>
  )
}

/** Blank is a cleared field, not an empty string. */
function orNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** One entry per line, blanks dropped. */
function lines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function useSaver(siteId: string | undefined) {
  const save = useSaveOutreachConfig(siteId)
  async function submit(patch: OutreachConfigInput, message = 'Saved') {
    try {
      await save.mutateAsync(patch)
      toast.success(message)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save that')
    }
  }
  return { submit, pending: save.isPending }
}

function Identity({
  siteId,
  config,
  maxDailyCap,
}: {
  siteId: string | undefined
  config: OutreachConfig | null
  maxDailyCap: number
}) {
  const { submit, pending } = useSaver(siteId)
  const ids = useId()

  const [description, setDescription] = useState(config?.description ?? '')
  const [marketNotes, setMarketNotes] = useState(config?.marketNotes ?? '')
  const [emailTemplate, setEmailTemplate] = useState(config?.emailTemplate ?? '')
  const [senderName, setSenderName] = useState(config?.senderName ?? '')
  const [senderRole, setSenderRole] = useState(config?.senderRole ?? '')
  const [senderEmail, setSenderEmail] = useState(config?.senderEmail ?? '')
  const [unsubscribeText, setUnsubscribeText] = useState(config?.unsubscribeText ?? '')
  const [dailyCap, setDailyCap] = useState(String(config?.dailyCap ?? 3))

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        const cap = Number.parseInt(dailyCap, 10)
        submit({
          description: orNull(description),
          marketNotes: orNull(marketNotes),
          emailTemplate: orNull(emailTemplate),
          senderName: orNull(senderName),
          senderRole: orNull(senderRole),
          senderEmail: orNull(senderEmail),
          unsubscribeText: orNull(unsubscribeText),
          ...(Number.isFinite(cap) ? { dailyCap: cap } : {}),
        })
      }}
    >
      <Panel
        title="This site"
        actions={
          <Button type="submit" size="sm" disabled={pending}>
            Save
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Description" htmlFor={`${ids}-description`}>
            <textarea
              id={`${ids}-description`}
              value={description}
              rows={3}
              onChange={(event) => setDescription(event.target.value)}
              className={FIELD}
            />
          </Field>

          <Field label="Market notes" htmlFor={`${ids}-market`}>
            <textarea
              id={`${ids}-market`}
              value={marketNotes}
              rows={5}
              onChange={(event) => setMarketNotes(event.target.value)}
              className={FIELD}
            />
          </Field>

          <Field label="Email template" htmlFor={`${ids}-template`}>
            <textarea
              id={`${ids}-template`}
              value={emailTemplate}
              rows={10}
              onChange={(event) => setEmailTemplate(event.target.value)}
              className={FIELD}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Sender name" htmlFor={`${ids}-name`}>
              <Input
                id={`${ids}-name`}
                value={senderName}
                onChange={(event) => setSenderName(event.target.value)}
                className="h-9 text-[13px]"
              />
            </Field>
            <Field label="Role" htmlFor={`${ids}-role`}>
              <Input
                id={`${ids}-role`}
                value={senderRole}
                onChange={(event) => setSenderRole(event.target.value)}
                className="h-9 text-[13px]"
              />
            </Field>
            <Field label="Reply address" htmlFor={`${ids}-email`}>
              <Input
                id={`${ids}-email`}
                type="email"
                value={senderEmail}
                onChange={(event) => setSenderEmail(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="h-9 font-mono text-[13px]"
              />
            </Field>
            <Field label={`Daily limit (max ${maxDailyCap})`} htmlFor={`${ids}-cap`}>
              <Input
                id={`${ids}-cap`}
                type="number"
                min={1}
                max={maxDailyCap}
                value={dailyCap}
                onChange={(event) => setDailyCap(event.target.value)}
                className="h-9 w-28 font-mono text-[13px] tabular"
              />
            </Field>
          </div>

          <Field label="Opt-out line" htmlFor={`${ids}-unsubscribe`}>
            <Input
              id={`${ids}-unsubscribe`}
              value={unsubscribeText}
              onChange={(event) => setUnsubscribeText(event.target.value)}
              className="h-9 text-[13px]"
            />
          </Field>
        </div>
      </Panel>
    </form>
  )
}

/**
 * The pages the mail may offer, matched out of the sitemap. Saving the patterns
 * re-reads the sitemap, so the list under them is what they now match.
 */
function Targets({ site, config }: { site: Site | undefined; config: OutreachConfig | null }) {
  const siteId = site?.id
  const { submit, pending } = useSaver(siteId)
  const ids = useId()
  const { data } = useOutreachTargets(site)
  const toggle = useSetTargetEnabled(siteId)
  const pin = usePinTarget(siteId)

  const [patterns, setPatterns] = useState((config?.targetPatterns ?? []).join('\n'))
  const [fallback, setFallback] = useState(config?.fallbackUrl ?? '')
  const [pinning, setPinning] = useState('')
  const [filter, setFilter] = useState('')

  const targets = data?.targets ?? []
  const shown = filter
    ? targets.filter((row) =>
        `${row.label} ${row.url}`.toLowerCase().includes(filter.toLowerCase()),
      )
    : targets

  return (
    <Panel
      title="Link targets"
      meta={
        targets.length > 0 ? formatCount(targets.filter((row) => row.enabled).length) : undefined
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          submit({ targetPatterns: lines(patterns), fallbackUrl: orNull(fallback) })
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Pages matching" htmlFor={`${ids}-patterns`}>
            <textarea
              id={`${ids}-patterns`}
              value={patterns}
              rows={3}
              onChange={(event) => setPatterns(event.target.value)}
              placeholder="/*-esim"
              spellCheck={false}
              className={cn(FIELD, 'font-mono')}
            />
          </Field>
          <Field label="Otherwise" htmlFor={`${ids}-fallback`}>
            <Input
              id={`${ids}-fallback`}
              value={fallback}
              onChange={(event) => setFallback(event.target.value)}
              spellCheck={false}
              className="h-9 font-mono text-[13px]"
            />
          </Field>
        </div>
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={pending}>
            Save
          </Button>
        </div>
      </form>

      {targets.length > 0 && (
        <div className="mt-5 space-y-3 border-t border-border pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter"
              className="h-8 w-48 text-[13px]"
            />
            <form
              className="ml-auto flex items-center gap-2"
              onSubmit={async (event) => {
                event.preventDefault()
                try {
                  await pin.mutateAsync({ url: pinning })
                  setPinning('')
                  toast.success('Added')
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : 'Could not add that')
                }
              }}
            >
              <Input
                value={pinning}
                onChange={(event) => setPinning(event.target.value)}
                placeholder="https://"
                spellCheck={false}
                className="h-8 w-64 font-mono text-[12px]"
              />
              <Button
                type="submit"
                size="sm"
                variant="outline"
                disabled={!pinning || pin.isPending}
              >
                Add
              </Button>
            </form>
          </div>

          <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-md border border-border">
            {shown.map((row) => (
              <li key={row.id} className="flex items-center gap-3 px-3 py-1.5 text-[13px]">
                <input
                  type="checkbox"
                  aria-label={row.label}
                  checked={row.enabled}
                  disabled={toggle.isPending}
                  onChange={(event) =>
                    toggle.mutate({ targetId: row.id, enabled: event.target.checked })
                  }
                  className="size-3.5 shrink-0 cursor-pointer accent-plum"
                />
                <span
                  className={cn('w-40 shrink-0 truncate', !row.enabled && 'text-muted-foreground')}
                >
                  {row.kind === 'fallback' ? 'Otherwise' : row.label}
                </span>
                <a
                  href={row.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="min-w-0 flex-1 truncate font-mono text-[12px] text-muted-foreground underline-offset-2 hover:underline"
                >
                  {bareUrl(row.url)}
                </a>
                {row.offered > 0 && (
                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular">
                    {row.offered}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  )
}

/** The searches that find pages: phrases per target, and the site's own name. */
function Finding({
  siteId,
  config,
}: {
  siteId: string | undefined
  config: OutreachConfig | null
}) {
  const { submit, pending } = useSaver(siteId)
  const ids = useId()
  const [templates, setTemplates] = useState((config?.sweepTemplates ?? []).join('\n'))
  const [brands, setBrands] = useState((config?.brandTerms ?? []).join('\n'))
  const [focus, setFocus] = useState((config?.focusTerms ?? []).join('\n'))

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        submit({
          sweepTemplates: lines(templates),
          brandTerms: lines(brands),
          focusTerms: lines(focus),
        })
      }}
    >
      <Panel
        title="Searches"
        actions={
          <Button type="submit" size="sm" disabled={pending}>
            Save
          </Button>
        }
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Per target" htmlFor={`${ids}-templates`}>
            <textarea
              id={`${ids}-templates`}
              value={templates}
              rows={5}
              onChange={(event) => setTemplates(event.target.value)}
              placeholder={'{name} itinerary\n{name} travel tips blog'}
              spellCheck={false}
              className={cn(FIELD, 'font-mono')}
            />
          </Field>
          <Field label="Mentions of" htmlFor={`${ids}-brands`}>
            <textarea
              id={`${ids}-brands`}
              value={brands}
              rows={5}
              onChange={(event) => setBrands(event.target.value)}
              spellCheck={false}
              className={cn(FIELD, 'font-mono')}
            />
          </Field>
          <Field label="On their page, look for" htmlFor={`${ids}-focus`}>
            <textarea
              id={`${ids}-focus`}
              value={focus}
              rows={5}
              onChange={(event) => setFocus(event.target.value)}
              spellCheck={false}
              className={cn(FIELD, 'font-mono')}
            />
          </Field>
        </div>
      </Panel>
    </form>
  )
}

/** The two switches, saved the moment they move. */
function Sending({
  siteId,
  config,
}: {
  siteId: string | undefined
  config: OutreachConfig | null
}) {
  const { submit, pending } = useSaver(siteId)
  const enabled = config?.enabled ?? false
  const dryRun = config?.dryRun ?? true

  return (
    <Panel title="Sending">
      <div className="space-y-3">
        <Toggle
          label="Outreach is on"
          checked={enabled}
          disabled={pending}
          onChange={(next) =>
            submit({ enabled: next }, next ? 'Outreach is on' : 'Outreach is off')
          }
        />
        <Toggle
          label="Draft everything, send nothing"
          checked={dryRun}
          disabled={pending}
          onChange={(next) =>
            submit({ dryRun: next }, next ? 'Nothing will be sent' : 'Approved mail will be sent')
          }
        />
        {config?.pausedAt && (
          <p className="font-mono text-[12px] text-warning">
            paused {config.pausedReason ? `— ${config.pausedReason}` : ''}
          </p>
        )}
      </div>
    </Panel>
  )
}

function Toggle({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string
  checked: boolean
  disabled: boolean
  onChange: (next: boolean) => void
}) {
  const id = useId()
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2.5 text-[13px]">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="size-3.5 shrink-0 cursor-pointer accent-plum"
      />
      <span>{label}</span>
    </label>
  )
}

/**
 * Where the mail goes out from and where the replies come back to.
 *
 * The passwords are a form of their own because they are written through a
 * route of their own: a wrong host is a bad send, where a leaked mailbox
 * password is somebody else's mailbox. Nothing here ever renders a stored
 * secret — what it renders is whether one is stored.
 */
function Mailbox({
  siteId,
  config,
}: {
  siteId: string | undefined
  config: OutreachConfig | null
}) {
  const save = useSaveOutreachConfig(siteId)
  const ids = useId()

  const [smtpHost, setSmtpHost] = useState(config?.smtpHost ?? '')
  const [smtpPort, setSmtpPort] = useState(config?.smtpPort ? String(config.smtpPort) : '')
  const [smtpUser, setSmtpUser] = useState(config?.smtpUser ?? '')
  const [imapHost, setImapHost] = useState(config?.imapHost ?? '')
  const [imapPort, setImapPort] = useState(config?.imapPort ? String(config.imapPort) : '')
  const [imapUser, setImapUser] = useState(config?.imapUser ?? '')

  function port(value: string): number | null {
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    try {
      await save.mutateAsync({
        smtpHost: orNull(smtpHost),
        smtpPort: port(smtpPort),
        smtpUser: orNull(smtpUser),
        imapHost: orNull(imapHost),
        imapPort: port(imapPort),
        imapUser: orNull(imapUser),
      })
      toast.success('Saved')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save that')
    }
  }

  return (
    <form onSubmit={submit}>
      <Panel
        title="Mailbox"
        actions={
          <Button type="submit" size="sm" disabled={save.isPending}>
            Save
          </Button>
        }
      >
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)]">
            <Field label="Outgoing host" htmlFor={`${ids}-smtp-host`}>
              <Input
                id={`${ids}-smtp-host`}
                value={smtpHost}
                onChange={(event) => setSmtpHost(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="h-9 font-mono text-[13px]"
              />
            </Field>
            <Field label="Port" htmlFor={`${ids}-smtp-port`}>
              <Input
                id={`${ids}-smtp-port`}
                type="number"
                min={1}
                max={65535}
                value={smtpPort}
                onChange={(event) => setSmtpPort(event.target.value)}
                className="h-9 font-mono text-[13px] tabular"
              />
            </Field>
            <Field label="User" htmlFor={`${ids}-smtp-user`}>
              <Input
                id={`${ids}-smtp-user`}
                value={smtpUser}
                onChange={(event) => setSmtpUser(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="h-9 font-mono text-[13px]"
              />
            </Field>
          </div>

          <Secret
            siteId={siteId}
            field="smtpPassword"
            label="Outgoing password"
            stored={config?.smtpConfigured ?? false}
          />

          <div className="grid gap-4 border-t border-border pt-5 sm:grid-cols-[minmax(0,1fr)_100px_minmax(0,1fr)]">
            <Field label="Incoming host" htmlFor={`${ids}-imap-host`}>
              <Input
                id={`${ids}-imap-host`}
                value={imapHost}
                onChange={(event) => setImapHost(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="h-9 font-mono text-[13px]"
              />
            </Field>
            <Field label="Port" htmlFor={`${ids}-imap-port`}>
              <Input
                id={`${ids}-imap-port`}
                type="number"
                min={1}
                max={65535}
                value={imapPort}
                onChange={(event) => setImapPort(event.target.value)}
                className="h-9 font-mono text-[13px] tabular"
              />
            </Field>
            <Field label="User" htmlFor={`${ids}-imap-user`}>
              <Input
                id={`${ids}-imap-user`}
                value={imapUser}
                onChange={(event) => setImapUser(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="h-9 font-mono text-[13px]"
              />
            </Field>
          </div>

          <Secret
            siteId={siteId}
            field="imapPassword"
            label="Incoming password"
            stored={config?.imapConfigured ?? false}
          />
        </div>
      </Panel>
    </form>
  )
}

/**
 * A password is written and never read back, so the field is always empty and
 * the only thing beside it is whether one is stored. Showing dots would be a
 * claim about a value we do not hold.
 */
function Secret({
  siteId,
  field,
  label,
  stored,
}: {
  siteId: string | undefined
  field: 'smtpPassword' | 'imapPassword'
  label: string
  stored: boolean
}) {
  const save = useSaveOutreachSecrets(siteId)
  const [value, setValue] = useState('')
  const id = useId()

  async function write(next: string, message: string) {
    try {
      await save.mutateAsync({ [field]: next })
      setValue('')
      toast.success(message)
    } catch {
      toast.error('Could not save that')
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field label={label} htmlFor={id} className="min-w-[220px] flex-1">
        <Input
          id={id}
          type="password"
          value={value}
          autoComplete="new-password"
          placeholder={stored ? 'Replace' : ''}
          onChange={(event) => setValue(event.target.value)}
          className="h-9 font-mono text-[13px]"
        />
      </Field>

      <span className="mono-label pb-2.5 text-muted-foreground">
        {stored ? 'stored' : 'not set'}
      </span>

      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={save.isPending || value.length === 0}
        onClick={() => write(value, 'Saved')}
      >
        Save
      </Button>

      {stored && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={save.isPending}
          onClick={() => write('', 'Removed')}
        >
          Remove
        </Button>
      )}
    </div>
  )
}
